import { useCallback } from 'react'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import {
  AccountRecord,
  UpdateAccount,
  PLG_TYPE,
  PlgDetails,
  GetAccountsResponse,
  JoinReadOnlyAccountRequest,
  NavFavoriteRequest,
  NavFavoritesResponse,
  UpdateNavFavoritesSequenceRequest,
  ListSavedFiltersRequest,
  ListSavedFiltersResponse,
  CreateSavedFilterRequest,
  UpdateSavedFilterRequest,
  DeleteSavedFilterRequest,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import log from 'loglevel'
import { useAuth } from 'src/context/authContext'
import { useGetRoleAccessConfig } from 'src/app/AppAuth/AppAuth.hooks'
import { GrpcResponse, NewGrpcResponse } from '../hooks/types'
import { useSwrImmutableGrpc } from '../useSwrImmutableGrpc'
import { useSwrGrpc } from '../useSwrGrpc'

export const useAccount = (): GrpcResponse<AccountRecord> => {
  const { response } = useSwrImmutableGrpc(
    AccountPromiseClient.prototype.get,
    new Empty(),
  )
  return NewGrpcResponse(response)
}

// True when this account is explicitly linked to a content account. False while the
// account request is in flight, so content-sourced affordances never flash in.
export const useHasContentAccount = (): boolean => {
  const { data } = useAccount()
  return Boolean(data?.getContentaccountid())
}

export const useAccounts = (): GrpcResponse<GetAccountsResponse> => {
  const { response } = useSwrImmutableGrpc(
    AccountPromiseClient.prototype.getAccounts,
    new Empty(),
  )
  return NewGrpcResponse(response)
}

export const useNavFavorites = (
  shouldFetch = true,
): GrpcResponse<NavFavoritesResponse> => {
  const { response } = useSwrImmutableGrpc(
    AccountPromiseClient.prototype.listNavFavorites,
    new Empty(),
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useSetNavFavorite = (): ((
  navKey: string,
  favorite: boolean,
) => Promise<void>) => {
  const accountService = useAuthorizedGrpcClient(AccountPromiseClient)
  return useCallback(
    async (navKey: string, favorite: boolean) => {
      try {
        await accountService.setNavFavorite(
          new NavFavoriteRequest().setNavKey(navKey).setFavorite(favorite),
        )
      } catch (err) {
        log.error(`error when setting nav favorite. error: ${err}`)
        throw err
      }
    },
    [accountService],
  )
}

export const useRandomAccountForUser = (): string => {
  const { accountId } = useAuth()
  const response = useAccounts()

  return (
    response?.data
      ?.getAccountsList()
      .find((acc) => acc.getId() !== accountId)
      ?.getId() || ''
  )
}

export const useUpdateAccount = (): ((
  organization: string,
) => Promise<void>) => {
  const { mutate: mutateAccount } = useAccount()
  const accountService = useAuthorizedGrpcClient(AccountPromiseClient)
  return async (organization: string) => {
    if (organization.length) {
      const updatedAccount = await accountService.update(
        new UpdateAccount().setOrgName(
          new StringValue().setValue(organization),
        ),
      )
      await mutateAccount(updatedAccount, false)
    }
  }
}

export const useUpdatePlgDetails = (): (({
  plgType,
  completionTime,
}: {
  plgType?: PLG_TYPE
  completionTime?: Timestamp
}) => Promise<void>) => {
  const accountService = useAuthorizedGrpcClient(AccountPromiseClient)
  return async ({ plgType, completionTime }) => {
    try {
      const request = new PlgDetails()
      if (completionTime !== undefined) {
        request.setCompletionTime(completionTime)
      }
      if (plgType !== undefined) {
        request.setPlgType(plgType)
      }
      await accountService.updatePlgDetails(request)
    } catch (err) {
      log.error(`error when updating account PLG details. error: ${err}`)
    }
  }
}

export const useReadonlyAccounts = (): GrpcResponse<GetAccountsResponse> => {
  // don't show allow auditors to see readonly accounts
  const {
    routeConfig: { restrictToAuditView },
  } = useGetRoleAccessConfig()

  const { response } = useSwrImmutableGrpc(
    AccountPromiseClient.prototype.getReadOnlyAccounts,
    new Empty(),
    !restrictToAuditView,
  )
  return NewGrpcResponse(response)
}

export const useIsReadonlyAccount = (): boolean => {
  const { accountId } = useAuth()
  const response = useReadonlyAccounts()
  return (
    response?.data
      ?.getAccountsList()
      .map((ele) => ele.getId())
      .includes(accountId) || false
  )
}

export const useJoinReadonlyAccount = (): (({
  accountId,
}: {
  accountId: string
}) => Promise<void>) => {
  const { mutate: mutateAccounts } = useAccounts()
  const { mutate: mutateReadonlyAccounts } = useReadonlyAccounts()
  const accountService = useAuthorizedGrpcClient(AccountPromiseClient)
  return useCallback(
    async ({ accountId }) => {
      try {
        await accountService.joinReadOnlyAccount(
          new JoinReadOnlyAccountRequest().setAccountId(accountId),
        )
        await mutateAccounts(undefined, true)
        await mutateReadonlyAccounts(undefined, true)
      } catch (err) {
        log.error(`error when joining readonly account. error: ${err}`)
      }
    },
    [mutateAccounts, mutateReadonlyAccounts, accountService],
  )
}

export const useUpdateNavFavoritesSequence = (): ((
  navKeys: string[],
) => Promise<void>) => {
  const accountService = useAuthorizedGrpcClient(AccountPromiseClient)
  return useCallback(
    async (navKeys: string[]) => {
      try {
        await accountService.updateNavFavoritesSequence(
          new UpdateNavFavoritesSequenceRequest().setNavKeysList(navKeys),
        )
      } catch (err) {
        log.error(`error when updating nav favorites sequence. error: ${err}`)
        throw err
      }
    },
    [accountService],
  )
}

export const useListSavedFilters = (
  route: string,
): GrpcResponse<ListSavedFiltersResponse> => {
  const { response } = useSwrGrpc(
    AccountPromiseClient.prototype.listSavedFilters,
    new ListSavedFiltersRequest().setRoute(route),
    !!route,
  )
  return NewGrpcResponse(response)
}

export const useCreateSavedFilter = (): ((
  route: string,
  name: string,
  paramsJson: string,
) => Promise<void>) => {
  const accountService = useAuthorizedGrpcClient(AccountPromiseClient)
  return useCallback(
    async (route: string, name: string, paramsJson: string) => {
      try {
        await accountService.createSavedFilter(
          new CreateSavedFilterRequest()
            .setRoute(route)
            .setName(name)
            .setParamsJson(paramsJson),
        )
      } catch (err) {
        log.error(`error creating saved filter: ${err}`)
        throw err
      }
    },
    [accountService],
  )
}

export const useUpdateSavedFilter = (): ((
  id: string,
  name: string,
  paramsJson: string,
) => Promise<void>) => {
  const accountService = useAuthorizedGrpcClient(AccountPromiseClient)
  return useCallback(
    async (id: string, name: string, paramsJson: string) => {
      try {
        await accountService.updateSavedFilter(
          new UpdateSavedFilterRequest()
            .setId(id)
            .setName(name)
            .setParamsJson(paramsJson),
        )
      } catch (err) {
        log.error(`error updating saved filter: ${err}`)
        throw err
      }
    },
    [accountService],
  )
}

export const useDeleteSavedFilter = (): ((id: string) => Promise<void>) => {
  const accountService = useAuthorizedGrpcClient(AccountPromiseClient)
  return useCallback(
    async (id: string) => {
      try {
        await accountService.deleteSavedFilter(
          new DeleteSavedFilterRequest().setId(id),
        )
      } catch (err) {
        log.error(`error deleting saved filter: ${err}`)
        throw err
      }
    },
    [accountService],
  )
}
